import './coapplicantDifferentResidencePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: {
      coapplicantCurrentResidenceCityAsString,
      coapplicantCurrentResidenceLineOneAsString,
      coapplicantCurrentResidenceLineTwoAsString,
      coapplicantCurrentResidencePostalCodeAsString,
      coapplicantCurrentResidenceStateAsString,
      isCoapplicantPersisted,
    },
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceCity,
    setCoapplicantCurrentResidenceLineOne,
    setCoapplicantCurrentResidenceLineTwo,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    setCoapplicantCurrentResidencePostalCode,
    setCoapplicantCurrentResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: coapplicantCurrentResidenceCityAsString,
      housingAmountValue: inputEventHandler.currency.formatter(
        coapplicantCurrentResidenceMonthlyPaymentAmount,
      ),
      lineOneValue: coapplicantCurrentResidenceLineOneAsString,
      lineTwoValue: coapplicantCurrentResidenceLineTwoAsString,
      postalCodeValue: coapplicantCurrentResidencePostalCodeAsString,
      stateValue: coapplicantCurrentResidenceStateAsString,
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      housingAmountValue,
      lineOneValue,
      lineTwoValue,
      postalCodeValue,
      stateValue,
    } = values;

    if (
      !cityValue ||
      !housingAmountValue ||
      !lineOneValue ||
      !postalCodeValue ||
      !stateValue
    ) {
      return;
    }

    setCoapplicantCurrentResidenceCity(cityValue);
    setCoapplicantCurrentResidenceLineOne(lineOneValue);
    setCoapplicantCurrentResidenceLineTwo(lineTwoValue);
    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCoapplicantCurrentResidencePostalCode(postalCodeValue);
    setCoapplicantCurrentResidenceState(stateValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-different-residence-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantDifferentResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantDifferentResidence.form.h1
                }
              >
                {t('CoapplicantDifferentResidencePage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantDifferentResidence.form.h2
                }
              >
                {t('CoapplicantDifferentResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantDifferentResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    error={!isEmpty(errors.housingAmountValue)}
                    label={t('CoapplicantDifferentResidencePage.housingAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantDifferentResidence.form
                            .amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.coapplicantDifferentResidence.form
                          .amountInputError
                      }
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.coapplicantDifferentResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t(
                      'CoapplicantDifferentResidencePage.residenceLineOne',
                    )}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantDifferentResidence.form
                            .addressLine1Input,
                      },
                    }}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        label={t(
                          'CoapplicantDifferentResidencePage.residenceLineTwo',
                        )}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantDifferentResidence.form
                                .addressLine2Input,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('cityValue', cityValidation)}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.cityValue)}
                        label={t(
                          'CoapplicantDifferentResidencePage.residenceCity',
                        )}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('cityValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantDifferentResidence.form
                                .cityInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.cityValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.coapplicantDifferentResidence.form
                              .cityInputError
                          }
                        >
                          {toString(errors.cityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantDifferentResidence.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantDifferentResidence.form
                            .stateSelect
                        }
                        label={t(
                          'CoapplicantDifferentResidencePage.residenceState',
                        )}
                        name="stateValue"
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                        rules={stateValidation}
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('postalCodeValue', postalCodeValidation)}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.postalCodeValue)}
                        label={t('CoapplicantDifferentResidencePage.zip')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('postalCodeValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantDifferentResidence.form
                                .zipCodeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.postalCodeValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.coapplicantDifferentResidence.form
                              .zipCodeInputError
                          }
                        >
                          {toString(errors.postalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantDifferentResidence.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantDifferentResidencePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantDifferentResidence.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantDifferentResidencePage';
